import React from "react";
import tailwindClassnames from "../common/TailwindClassnames";
import { BeatLoader } from "react-spinners";
import { t } from "i18next";
import { EventHistoryItem } from "../common/ObjectTypes";
import {
  eventStatusChipClass,
  formatTimestampToInternational,
} from "../common/helpers";

type DeploymentEventHistoryProps = {
  selectedDeployment: any;
  eventHistory: EventHistoryItem[];
  loadingEventHistory: boolean;
};

/**
 * Read-only import/upload event history table for the selected deployment,
 * rendered as a table card (rows are not clickable, so no row cursor/hover
 * affordance is applied).
 */
const DeploymentEventHistory: React.FC<DeploymentEventHistoryProps> = ({
  selectedDeployment,
  eventHistory,
  loadingEventHistory,
}) => {
  return (
    <div className="mt-4 w-full">
      <div className={tailwindClassnames.tableCard}>
        <div className="flex flex-row items-center justify-between gap-4 border-b border-off-white-dark/60 px-4 py-3 transition duration-300 dark:border-storm-blue-border">
          <span className="text-sm font-semibold text-primary-off-black transition duration-300 dark:text-primary-off-white">
            {t("Activity")}
          </span>
          <span className="text-xs text-off-black-light transition duration-300 dark:text-greyed-text">
            {t("Imports and uploads")}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr>
                <th className={tailwindClassnames.tableCardHeadCell}>
                  {t("Action")}
                </th>
                <th className={tailwindClassnames.tableCardHeadCell}>
                  {t("Status")}
                </th>
                <th
                  className={`${tailwindClassnames.tableCardHeadCell} text-right`}
                >
                  {t("Number of Files")}
                </th>
                <th className={tailwindClassnames.tableCardHeadCell}>
                  {t("User")}
                </th>
                <th
                  className={`${tailwindClassnames.tableCardHeadCell} text-right`}
                >
                  {t("Started at")}
                </th>
                <th
                  className={`${tailwindClassnames.tableCardHeadCell} text-right`}
                >
                  {t("Ended at")}
                </th>
              </tr>
            </thead>
            <tbody>
              {loadingEventHistory ? (
                <tr>
                  <td
                    colSpan={6}
                    className="text-center text-off-black-light dark:text-off-white-dark py-4 px-4"
                  >
                    <BeatLoader
                      color={tailwindClassnames.skyBlueDark}
                      loading={true}
                      size={10}
                      margin={3}
                    />
                  </td>
                </tr>
              ) : eventHistory.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="text-center text-off-black-light dark:text-off-white-dark py-4 px-4 transition duration-300"
                  >
                    {t("No event history found.")}
                  </td>
                </tr>
              ) : (
                eventHistory.map((event, idx) => (
                  <tr
                    key={idx}
                    className={`${tailwindClassnames.tableCardRowStatic} ${tailwindClassnames.noHover}`}
                  >
                    <td className={tailwindClassnames.tableCardCell}>
                      {t(event.type)}
                    </td>
                    <td className={tailwindClassnames.tableCardCell}>
                      <span className={eventStatusChipClass(event.status)}>
                        {t(event.status)}
                      </span>
                    </td>
                    <td
                      className={`${tailwindClassnames.tableCardCell} text-right tabular-nums`}
                    >
                      {event.file_count ?? "—"}
                    </td>
                    <td
                      className={tailwindClassnames.tableCardCell}
                      title={event.user_name || undefined}
                    >
                      {event.user_name}
                    </td>
                    <td
                      className={`${tailwindClassnames.tableCardCell} text-right tabular-nums`}
                    >
                      {formatTimestampToInternational(event.started_at)}
                    </td>
                    <td
                      className={`${tailwindClassnames.tableCardCell} text-right tabular-nums`}
                    >
                      {formatTimestampToInternational(event.ended_at)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* What the count means (AKL-1074). An upload is registered with the
            files selected. When it ends, the website and Akili Transfer both send
            the files it actually uploaded, so a row reads the same whichever tool
            made it. A run ended from this page (End upload, AKL-897) or closed by
            the API (expired or taken over) keeps the count it was registered
            with. */}
        <p
          data-testid="event-history-count-note"
          className="border-t border-off-white-dark/60 px-4 py-2 text-xs text-off-black-light transition duration-300 dark:border-storm-blue-border dark:text-greyed-text"
        >
          {t(
            "Number of Files: for an upload, the files it sent to storage, not counting files already there. While it runs, or if it was ended from this page or closed by akili, the files selected. For an import, the files it brings into akili.",
          )}
        </p>
      </div>
    </div>
  );
};

export default DeploymentEventHistory;
